<template>
  <div class="admin-survey-show" v-if="activeSurvey.id">
    <div class="admin-survey-show__details panel">
      <p>Created on {{ formatDateShort(activeSurvey.created_at) }}</p>
      <div class="admin-survey-show__stats">
        <div><span class="stat">{{ activeSurvey.question_count }}</span> questions</div>
        <div><span class="stat">{{ activeSurvey.page_count }}</span> pages</div>
      </div>
      <router-link :to="{ name: 'admin-design-survey' }" class="button">Edit Design</router-link>
      <router-link :to="{ name: 'admin-preview-survey' }" class="button">Preview Survey</router-link>
    </div>

    <div class="admin-survey-show__overview">
      <div class="admin-survey-show__overview-box">
        <div class="panel">
          <div>Total responses</div>
          <div><span class="stat">{{ numberOfResponses }}</span></div>
        </div>
      </div>

      <div class="admin-survey-show__overview-box">
        <div class="panel">
          <div>Survey Status</div>
          <div><span class="stat">{{ activeSurvey.published ? 'Published' : 'Unpublished' }}</span></div>
        </div>
      </div>

      <label class="show-archived-responses">
        Show Archived Responses?
        <div class="input input--checkbox input--spacing">
          <input
            type="checkbox"
            class="input__default"
            :checked="showArchivedSurveyResponses"
            @change="setShowArchivedSurveyResponses($event.target.checked)">
          <div class="input__styled input__styled--checkbox" :class="{'is-checked': showArchivedSurveyResponses}"></div>
        </div>
      </label>

      <div
        class="admin-survey-show__overview-box admin-survey-show__overview-box--full"
        v-if="sortedResponses.length > 0">
          <div class="title-actions">
            <div class="flex flex--column">
              <h2 class="no-margin">Responses</h2>
              <div class="note flex flex--vert-center" v-if="$refs.paginateResponses">
                <div class="full-width">{{$refs.paginateResponses.pageItemsCount}}</div>
                <paginate-links
                  v-if="numberOfResponses > 10"
                  for="responses"
                  :show-step-links="true"
                  :simple="{ prev: 'Prev', next: 'Next' }" />
              </div>
            </div>
            <div class="actions">
              <pulse-loader :loading="showLoadingSpinner" :color="primaryColour" class="v-spinner--no-spacing actions__action" />
              <export-download-link>
                <template v-slot:default="exportLink">
                  <div class="actions__action actions__action--left flex flex--vert-center">
                    <button class="actions__action button" @click="exportLink.requestDownload">
                      Export to Excel
                    </button>
                  </div>
                </template>
              </export-download-link>
              <router-link :to="{ name: 'admin-survey-analyse' }" class="actions__action button">
                Analyse Responses
              </router-link>
            </div>
          </div>

          <paginate
            name="responses"
            ref="paginateResponses"
            :list="sortedResponses"
            :per="10"
            tag="div"
            class="list-view"
            v-on:start-loading="$emit('start-loading')"
            v-on:stop-loading="$emit('stop-loading')">
              <div class="list-view__header">
                <div>Submitted By</div>
                <div>Completed On</div>
                <div class="list-view__small-column">Actions</div>
              </div>
              <div class="list-view__row" v-for="response in paginated('responses')">
                <div>
                  {{ response.user_full_name || 'Anonymous' }}</router-link>
                  <template v-if="response.archived">&nbsp;<small>(archived)</small></template>
                </div>
                <div>
                  <router-link
                    :to="{
                      name: 'admin-survey-response',
                      params: { surveyId: activeSurvey.id, responseId: response.id }
                    }">
                      {{ formatDateLong(response.updated_at) }}
                  </router-link>
                </div>
                <div class="list-view__small-column">
                  <button class="icon-button" @click="archiveSurveyResponse(response)" :disabled="response.archived" title="Archive response">
                    <i class="material-icons">archive</i>
                  </button>
                  <export-download-link :response="response">
                    <template v-slot:default="exportLink">
                      <button class="icon-button" title="Download response" @click="exportLink.requestDownload">
                        <i class="material-icons">get_app</i>
                      </button>
                    </template>
                  </export-download-link>
                  <button v-if="response.actor_uid" class="icon-button" @click="addSurveyInvite(response)" :title="`Send invite to ${response.user_full_name}`">
                    <i class="material-icons">rsvp</i>
                  </button>
                  <template v-if="Object.keys(responseInviteIds).length > 0">
                    <router-link :to="inviteLink(response)" v-if="inviteLink(response)">
                      <i class="material-icons">share</i>
                    </router-link>
                    <i class="material-icons" style="width: 22px;" v-else></i>
                  </template>
                </div>
              </div>
          </paginate>
      </div>
      <div
        class="admin-survey-show__overview-box admin-survey-show__overview-box--full"
        v-else>
          <div class="panel">
            <em>No current responses</em>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
  import clone from 'lodash.clone';
  import PulseLoader from 'vue-spinner/src/PulseLoader';
  import DateFormatter from '@/mixins/date';
  import Common from '@/store/common';
  import ExportDownloadLink from '@/components/admin/surveys/ExportDownloadLink';

  export default {
    name: 'admin-show-survey',

    mixins: [DateFormatter],

    components: {
      PulseLoader,
      ExportDownloadLink,
    },

    data() {
      return {
        paginate: ['responses'],
        showLoadingSpinner: false,
        responseInviteIds: {},
      };
    },

    computed: {
      ...mapState([
        'primaryColour',
      ]),

      ...mapState({
        responses: state => state.admin.survey.responses,
        showArchivedSurveyResponses: state => state.admin.survey.showArchivedSurveyResponses,
      }),

      ...mapGetters('admin', [
        'activeSurvey',
      ]),

      sortedResponses() {
        if (this.responses) {
          return clone(this.responses).sort((a, b) => {
            return this.parseDateTime(b.updated_at) - this.parseDateTime(a.updated_at);
          }).filter(response => this.showArchivedSurveyResponses || !response.archived);
        }
        return [];
      },

      numberOfResponses() {
        return this.responses ? this.responses.length : 0;
      },
    },

    methods: {
      ...mapMutations('admin', [
        'setShowArchivedSurveyResponses',
      ]),

      ...mapActions('admin', [
        'addSurveyInvite',
      ]),

      archiveSurveyResponse(response) {
        Common.confirmDialog({
          modal: this.$modal,
          title: `Are you sure you want to archive the response dated ${this.formatDateLong(response.updated_at)}?`,
          okText: 'Yes',
          cancelText: 'No',
          okHandler: () => {
            this.$store.dispatch('admin/archiveSurveyResponse', { surveyResponseId: response.id });
          },
        });
      },

      addSurveyInvite(response) {
        Common.confirmDialog({
          modal: this.$modal,
          title: `Are you sure you want to send a new invite to ${response.user_full_name}?`,
          okText: 'Yes',
          cancelText: 'No',
          okHandler: () => {
            this.$store
              .dispatch('admin/addSurveyInvite', { surveyId: response.survey_id, actorUid: response.actor_uid })
              .then(invite => this.$set(this.responseInviteIds, response.id, invite.id));
          },
        });
      },

      inviteLink(response) {
        if (!this.responseInviteIds[response.id]) return false;

        return {
          name: 'survey-new',
          params: {
            surveyRef: this.activeSurvey.reference,
            surveyInviteId: this.responseInviteIds[response.id],
            hasLastResponse: true,
          },
        };
      },
    },

    created() {
      this.$on('start-loading', () => this.showLoadingSpinner = true);
      this.$on('stop-loading', () => this.showLoadingSpinner = false);
    },
  };
</script>

<style scoped>
  .show-archived-responses {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 15px;
    margin-left: auto;
    margin-right: 2px;

    .input {
      padding-right: 0;
    }
  }
</style>
